import { useMemo } from "react";
import { format } from "date-fns";
import { apiGet } from "../utils/apiClient";
import { mergeOperatorRows } from "../utils/TableHelpers/operatorTableData";

/**
 * Retrieves Operator Table data.
 * @returns - A promise that resolves to a combined, source-tagged response of
 * FixedRouteBusBlocks and SpecialRouteBusBlocks data.
 */
export const useFetchOperatorTableData = () => {
  const memoizedFetchOperatorTableData = useMemo(
    () => async (assignmentDate, rowFilter, pulloutDivision) => {
      const apiDate = format(assignmentDate, "dd-MMM-yyyy");
      const endpoint = `/${apiDate}/${rowFilter}/${pulloutDivision}`;

      // Fetch both datasets in parallel
      const [fixedResponse, specialResponse] = await Promise.all([
        apiGet(`/FixedRouteBusBlocks${endpoint}`, "fetching Fixed Route Bus Blocks"),
        apiGet(`/SpecialRouteBusBlocks${endpoint}`, "fetching Special Route Bus Blocks"),
      ]);

      // If both failed, return error
      if (!fixedResponse.success && !specialResponse.success) {
        return fixedResponse; // Return the error from one of them
      }

      // If only one failed, return the other (still tagged) so the board renders.
      if (!fixedResponse.success) {
        return { success: true, data: mergeOperatorRows([], specialResponse.data) };
      }
      if (!specialResponse.success) {
        return { success: true, data: mergeOperatorRows(fixedResponse.data, []) };
      }

      // Both succeeded, combine and tag the data
      return {
        success: true,
        data: mergeOperatorRows(fixedResponse.data, specialResponse.data),
      };
    },
    []
  );

  return memoizedFetchOperatorTableData;
};
